import { Skeleton, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TabsContent, TabsList, TabsTrigger, useValidationResultDialog } from '@nest/uikit-react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router';
import { useTerminalClient } from '../client-rpc-router';
import { StockItemLinkDetail } from '../components/StockItemLinkDetail';
import { TerminalScreen } from '../components/TerminalScreen';

export default function HandlingUnitDetailPage() {
	const client = useTerminalClient();
	const [searchParams] = useSearchParams();
	const code = searchParams.get('code');

	const [activeTab, setActiveTab] = useState('default');
	const [bottomElementRef, setBottomElementRef] = useState<HTMLDivElement | null>(null);

	const { data, error } = useQuery({
		queryKey: ['handling-unit', code],
		queryFn: () => client.wms.findHandlingUnitByCodeQuery.execute({ code: code! }),
		enabled: Boolean(code)
	});

	const { data: items, error: itemsError } = useQuery({
		queryKey: ['handling-unit-items', data?.id, activeTab],
		queryFn: () => client.wms.getHandlingUnitStockItemLocationsQuery.execute({ id: data!.id }),
		enabled: activeTab === 'items' && Boolean(data?.id)
	});

	useValidationResultDialog([error, itemsError]);

	return (
		<TerminalScreen showBackButton topBar={<div>{data?.code ?? 'Klec'}</div>} bottomContent={<div ref={setBottomElementRef} />}>
			<Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
				<TabsContent value="default" className="h-full">
					<Table>
						<TableBody>
							<TableRow>
								<TableHead className="w-1/2">Kód</TableHead>
								<TableCell className="w-1/2">{data?.code ?? code}</TableCell>
							</TableRow>
							{(data?.type?.code ?? data?.type?.fixedType) && (
								<TableRow>
									<TableHead className="w-1/2">Typ</TableHead>
									<TableCell className="w-1/2">{data?.type?.code ?? data?.type?.fixedType}</TableCell>
								</TableRow>
							)}
							{data?.storageLocation?.code && (
								<TableRow>
									<TableHead className="w-1/2">Lokace</TableHead>
									<TableCell className="w-1/2">{data.storageLocation.code}</TableCell>
								</TableRow>
							)}
							{data?.totalStockItemQuantity != null && (
								<TableRow>
									<TableHead className="w-1/2">Kusů zboží</TableHead>
									<TableCell className="w-1/2">{data.totalStockItemQuantity}</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TabsContent>
				<TabsContent value="items" className="h-full">
					<div className="p-2 flex flex-col gap-4 h-full overflow-auto">
						{items ? (
							items.length === 0 ? (
								<div className="p-6 text-center text-muted-foreground">Žádné položky</div>
							) : (
								<div className="grid grid-cols-1 gap-2">
									{items.map(loc => (
										<div key={loc.id} className="w-full border rounded p-2 text-left flex flex-col gap-1">
											<div className="font-semibold">
												<StockItemLinkDetail stockItemId={loc.stockItem.id} name={loc.stockItem.item.name} />
											</div>

											<div className="flex gap-2">
												{loc.batch != null && (
													<span className="bg-muted text-muted-foreground px-2 py-1 rounded-md">
														Šarže: {loc.originCountry} {loc.batch}
													</span>
												)}
												<div className="flex-1"></div>
												<span className="bg-muted text-muted-foreground px-2 py-1 rounded-md">Kusů: {loc.quantity}</span>
											</div>
										</div>
									))}
								</div>
							)
						) : (
							<div className="space-y-2">
								<Skeleton className="h-12 w-full" />
								<Skeleton className="h-12 w-full" />
								<Skeleton className="h-12 w-full" />
							</div>
						)}
					</div>
				</TabsContent>

				{bottomElementRef &&
					createPortal(
						<TabsList className="mt-auto w-full">
							<TabsTrigger value="default" className="w-full">
								<span>Základní</span>
							</TabsTrigger>
							<TabsTrigger value="items" className="w-full">
								<span>Zboží</span>
							</TabsTrigger>
						</TabsList>,
						bottomElementRef
					)}
			</Tabs>
		</TerminalScreen>
	);
}
